import { FC, SyntheticEvent } from 'react'
import { useIntl } from 'react-intl'
import axios from 'axios'
import ResultViewer from '../../utils/ResultViewer/ResultViewer'
import Filters from '../../utils/ResultViewer/Filters'
import Pagination from '../../utils/ResultViewer/Pagination'

type WrapperProps = {
  onEdit: (id: any, refresh: Function) => void
  onDelete: (id: any, refresh: Function) => void
  onViewResult: (id: any) => void
  onAdd?: (event: SyntheticEvent, refresh: Function) => void
  enablePolling?: boolean
  pollingInterval?: number
}

class SimulationsResultTable extends ResultViewer {
  private pollingTimer?: NodeJS.Timeout
  private lastDataHash?: string

  constructor(props: any) {
    super(props)
    this.pollingTimer = undefined
    this.lastDataHash = undefined
  }

  componentDidMount() {
    super.componentDidMount()
    const { enablePolling = false, pollingInterval = 5000 } = this.props as any
    if (enablePolling) {
      this.startPolling(pollingInterval)
    }
  }

  componentWillUnmount() {
    this.stopPolling()
  }

  startPolling(interval: number) {
    this.stopPolling()
    this.pollingTimer = setInterval(() => {
      this.pollData()
    }, interval)
  }

  stopPolling() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer)
      this.pollingTimer = undefined
    }
  }

  hashData(data: any): string {
    return JSON.stringify(data?.data || [])
  }

  pollData() {
    axios
      .get(this.props.dataUrl, {
        withCredentials: true,
        params: {
          page: this.state.page,
          orderBy: this.state.orderBy,
          orderDirection: this.state.orderDirection,
          ...this.state.filterParams,
        },
      })
      .then((response) => {
        let data = response.data.data
        const newHash = this.hashData(data)

        if (this.lastDataHash !== newHash) {
          this.lastDataHash = newHash
          data.search = Object.values(data.search)
          this.setState({
            data: data,
          })
        }
      })
      .catch((error) => {
        console.error('Polling error:', error)
      })
  }

  render() {
    let limit = this.state.data?.pagination.limit ?? 0
    let page = this.state.page ?? 0
    let totalCount = this.state.data?.pagination.total_count ?? 0
    let viewStart = (page - 1) * limit
    if (viewStart === 0) {
      viewStart = 1
    } else {
      viewStart = viewStart + 1
    }

    let viewEnd = page * limit
    if (viewEnd > totalCount) {
      viewEnd = totalCount
    }
    if (!this.state.data) {
      return <>Loading...</>
    } else {
      return (
        <div className='card mb-5 mb-xl-8'>
          <div className='card-header'>
            <h3 className='card-title align-items-start flex-column'>
              <span className='card-label fw-bold fs-3 mb-1'>{this.props.title}</span>
              <span className='text-muted mt-1 fw-semibold fs-7'>
                Out of {this.state.data?.pagination.total_count ?? 0} records {viewStart} -&nbsp;
                {viewEnd ?? 1} is monitoring.
              </span>
            </h3>
            <div className='align-self-end align-self-center'>
              <Filters
                search={this.state.data.search ?? []}
                setFilterParams={(name: string, value: any) => {
                  this.setFilterParams(name, value)
                }}
                applyFilter={() => {
                  this.fetchData()
                }}
                clearFilters={() => {
                  this.clearFilterParams()
                }}
              />
              {this.state.actionItems?.map((item, index) => {
                return (
                  <button
                    key={index}
                    className={item.class}
                    onClick={(event) => {
                      item.onClick(event, this.fetchData)
                    }}
                  >
                    <span className={item.icon + ' me-2'}></span>
                    {item.text}
                  </button>
                )
              })}
            </div>
          </div>
          <div className='card-body overflow-auto'>
            <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
              <thead>
                <tr>{this.renderHeaders()}</tr>
              </thead>
              <tbody>{this.renderRows()}</tbody>
            </table>
          </div>
          <div className='card-footer'>
            <Pagination {...this.state.data.pagination} setPage={this.setPage} />
          </div>
        </div>
      )
    }
  }

  renderHeaders() {
    if (this.state.data) {
      return Object.keys(this.state.data.headers).map((key) => {
        return this.renderHeader(key)
      })
    } else {
      return <></>
    }
  }

  renderHeader(key: string) {
    if (isNaN(parseInt(key))) {
      let direction =
        this.state.orderBy === key && this.state.orderDirection === 'DESC' ? 'ASC' : 'DESC'
      return (
        <th
          key={key}
          role='button'
          onClick={() => this.setOrderBy(key, direction)}
          className='pe-5'
        >
          <div className='d-flex justify-content-between'>
            {(this.state.data?.headers as any)[key]}
            <span
              className={
                'fa ' +
                (this.state.orderBy === key && direction === 'ASC'
                  ? 'fa-arrow-down'
                  : 'fa-arrow-up')
              }
            ></span>
          </div>
        </th>
      )
    }
    return <th key={key}>{(this.state.data?.headers as any)[key]}</th>
  }

  renderRows() {
    if (this.state.data) {
      return this.state.data.data.map((row: any, index) => {
        return (
          <tr key={index}>
            {Object.keys(row).map((key, colIndex) => {
              return this.renderRow(row, key, colIndex)
            })}
          </tr>
        )
      })
    } else {
      return <></>
    }
  }

  renderRow(row: any, key: string, colIndex: number) {
    if (key === 'edit_actions') {
      const status = row.status
      const isSuccess = status === 'success'
      const isProcessing = status === 'processing'
      const isFailed = status === 'failed'
      const canEdit = !isSuccess && !isProcessing

      return (
        <td key={colIndex}>
          <div className='d-flex justify-content-end flex-shrink-0'>
            {isSuccess && (
              <button
                onClick={() => (this.props as any).onViewResult(row[key])}
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                title='View Result'
              >
                <span className='fa fa-chart-line'></span>
              </button>
            )}
            {isFailed && (
              <button
                onClick={() => {
                  const id = row[key]
                  axios.get('/simulations/save/' + id).then((response) => {
                    let csrfData = response.data.data
                    axios
                      .post('/simulations/save/' + id, {
                        ...csrfData,
                        simulations: {
                          status: 'created',
                        },
                      })
                      .then(() => {
                        this.fetchData()
                      })
                  })
                }}
                className='btn btn-icon btn-bg-light btn-active-color-warning btn-sm me-1'
                title='Retry'
              >
                <span className='fa fa-redo'></span>
              </button>
            )}
            <button
              onClick={() => this.props.editCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
              title={canEdit ? 'Edit' : 'View Settings'}
            >
              <span className={`fa ${canEdit ? 'fa-edit' : 'fa-eye'}`}></span>
            </button>
            <button
              onClick={() => this.props.deleteCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1'
            >
              <span className='fa fa-trash'></span>
            </button>
          </div>
        </td>
      )
    }
    if (key === 'status') {
      const status = row[key]
      const label =
        status === 'created'
          ? 'Queued'
          : status === 'processing'
          ? 'Processing'
          : status === 'success'
          ? 'Success'
          : status === 'failed'
          ? 'Failed'
          : String(status)
      const badgeClass =
        status === 'created'
          ? 'badge badge-warning'
          : status === 'processing'
          ? 'badge badge-info'
          : status === 'success'
          ? 'badge badge-success'
          : status === 'failed'
          ? 'badge badge-danger'
          : 'badge badge-light'
      return (
        <td key={colIndex}>
          <span className={badgeClass}>{label}</span>
        </td>
      )
    }
    if (key === 'meter_type') {
      const type = row[key]
      const label = type === 'Electricity' ? 'Electricity' : type === 'Gas' ? 'Gas' : String(type)
      const badgeClass =
        type === 'Electricity'
          ? 'badge badge-primary'
          : type === 'Gas'
          ? 'badge badge-success'
          : 'badge badge-light'
      return (
        <td key={colIndex}>
          <span className={badgeClass}>{label}</span>
        </td>
      )
    }
    return (
      <td
        key={colIndex}
        dangerouslySetInnerHTML={{
          __html: row[key],
        }}
      ></td>
    )
  }
}

const SimulationsTable: FC<WrapperProps> = ({
  onEdit,
  onDelete,
  onViewResult,
  onAdd,
  enablePolling = false,
  pollingInterval = 5000,
}) => {
  const intl = useIntl()
  const ResultTable = SimulationsResultTable as any
  return (
    <ResultTable
      title={intl.formatMessage({ id: 'MENU.SIMULATIONS', defaultMessage: 'Simulations' })}
      dataUrl='/simulations/list'
      editCallback={onEdit}
      deleteCallback={onDelete}
      onViewResult={onViewResult}
      {...({ enablePolling, pollingInterval } as any)}
      actionItems={
        onAdd
          ? [
              {
                text: intl.formatMessage({
                  id: 'MENU.SIMULATIONS.ADD_SIMULATION',
                  defaultMessage: 'Add Simulation',
                }),
                class: 'btn btn-sm btn-primary',
                icon: 'fa fa-plus',
                onClick: onAdd,
              },
            ]
          : []
      }
    />
  )
}

export default SimulationsTable
